import React from 'react';
import { Box, Container, Typography, Paper, Divider } from '@mui/material';
import { PlayCircleOutline, Gavel } from '@mui/icons-material';
import { motion } from 'framer-motion';
import Footer from '../components/Footer/Footer';
import Header from '../components/Header/Header';

const Terms: React.FC = () => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
        display: 'flex',
        flexDirection: 'column',
      }}
>
      <Header />
      <Box sx={{ height: 72 }} />
      <Container maxWidth="md" sx={{ py: 8, flex: 1 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <PlayCircleOutline
              sx={{
                fontSize: { xs: 60, md: 80 },
                color: 'primary.main',
                mb: 3,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              Terms of Service
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Last updated: {new Date().toLocaleDateString()}
            </Typography>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
              <Gavel sx={{ mr: 1, verticalAlign: 'middle' }} />
              Agreement to Terms
            </Typography>
            
            <Typography paragraph>
              By accessing and using AutoReelr ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. These Terms of Service ("Terms") govern your use of the AutoReelr platform operated by Jonkai Ventures OÜ ("Company", "we", "our", or "us").
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              1. Description of Service
            </Typography>
            <Typography paragraph>
              AutoReelr is an AI-powered platform that enables users to create, manage, and publish faceless video content ("reels") across various social media platforms. Our service includes but is not limited to:
            </Typography>
            <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>AI-generated script creation using GPT technology</li>
              <li>Automated video assembly with stock footage</li>
              <li>Text-to-speech conversion using Azure TTS</li>
              <li>Multi-platform publishing capabilities</li>
              <li>Analytics and performance tracking</li>
              <li>Content scheduling and automation</li>
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              2. User Accounts and Responsibilities
            </Typography>
            <Typography paragraph>
              You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to:
            </Typography>
            <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>Provide accurate and complete information when creating your account</li>
              <li>Maintain the security of your login credentials</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Use the service in compliance with all applicable laws and regulations</li>
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              3. Acceptable Use Policy
            </Typography>
            <Typography paragraph>
              You agree not to use AutoReelr to:
            </Typography>
            <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>Create content that is illegal, harmful, threatening, abusive, or defamatory</li>
              <li>Infringe upon intellectual property rights of others</li>
              <li>Generate spam or unsolicited communications</li>
              <li>Attempt to reverse engineer or hack the platform</li>
              <li>Violate the terms of service of third-party platforms where content is published</li>
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              4. Intellectual Property Rights
            </Typography>
            <Typography paragraph>
              You retain ownership of the content you create using AutoReelr. However, by using our service, you grant us a non-exclusive, worldwide license to process, store, and transmit your content as necessary to provide the service. We reserve all rights to our platform, technology, and associated intellectual property.
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              5. Third-Party Services
            </Typography>
            <Typography paragraph>
              Our platform integrates with various third-party services including but not limited to OpenAI, Microsoft Azure, Pexels, and social media platforms. Your use of these services through our platform is subject to their respective terms of service and privacy policies.
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              6. Payment and Subscription Terms
            </Typography>
            <Typography paragraph>
              Paid features of AutoReelr are subject to our current pricing structure. Subscriptions automatically renew unless cancelled. Refunds are provided according to our refund policy. We reserve the right to modify pricing with 30 days notice to existing subscribers.
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              7. Service Availability and Modifications
            </Typography>
            <Typography paragraph>
              While we strive to maintain high availability, we do not guarantee uninterrupted access to our service. We reserve the right to modify, suspend, or discontinue any aspect of the service with reasonable notice.
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              8. Limitation of Liability
            </Typography>
            <Typography paragraph>
              To the maximum extent permitted by law, Jonkai Ventures OÜ shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, or goodwill, arising from your use of the service.
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              9. Data Protection and Privacy
            </Typography>
            <Typography paragraph>
              Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the service, to understand our practices regarding the collection, use, and disclosure of your personal information.
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              10. Termination
            </Typography>
            <Typography paragraph>
              Either party may terminate this agreement at any time. Upon termination, your right to use the service ceases immediately. We may retain your data as required by law or legitimate business interests.
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              11. Governing Law
            </Typography>
            <Typography paragraph>
              These Terms are governed by the laws of the Republic of Estonia. Any disputes shall be resolved in the courts of Estonia.
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              12. Changes to Terms
            </Typography>
            <Typography paragraph>
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting. Your continued use of the service constitutes acceptance of the modified Terms.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              If you have questions about these Terms, please contact us at info@autoreelr.com
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              <strong>Jonkai Ventures OÜ</strong><br />
              Tartu mnt 67/1-13b, 10115<br />
              Harju maakond, Tallinn, Kesklinna linnaosa<br />
              Registrikood 17322110 - Estonia EU
            </Typography>
          </Paper>
        </motion.div>
      </Container>

      <Footer />
    </Box>
  );
};

export default Terms;